import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({
  title,
  description,
  keywords,
  canonicalUrl,
  image,
  schema,
  breadcrumb
}) => {
  // Set defaults or use props
  const siteTitle = 'Rebaz AI';
  const defaultDescription = '值得信賴的 AI 客服助手，整合網站聊天與 LINE 官方帳號，自動回覆客戶問題、蒐集名單、推薦服務，減少人力負擔，提升客服品質。';
  const defaultImage = 'https://agent-rebaz-ai.s3.ap-northeast-1.amazonaws.com/Line_AI_Hero.png';
  const domain = 'https://rebaz.ai'; // Make sure to use absolute URLs

  // Build full page title
  const fullTitle = title ? `${title.replace(/\n/g, ' ')} | ${siteTitle}` : 'AI 客服助手 — Web Chat + LINE | Rebaz AI';

  // Ensure image URLs are absolute
  const absoluteImageUrl = image || defaultImage;
  const absoluteCanonicalUrl = canonicalUrl || domain;

  // Default keywords with AI Agent terms
  const defaultKeywords = 'AI 客服助手, Web Chat AI, LINE AI 助手, AI 客服, 網站聊天機器人, LINE 官方帳號 AI, 自動回覆, 客戶服務, 聊天機器人, 知識庫管理, 客服解決方案';

  // WebSite + Organization base schema (always present)
  const baseSchemas = [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Rebaz AI",
      "alternateName": "AI Customer Service for Web Chat + LINE",
      "url": domain,
      "description": defaultDescription,
      "inLanguage": ["zh-TW", "zh-CN", "en", "ja"],
      "publisher": {
        "@type": "Organization",
        "name": "Rebaz AI",
        "url": domain,
        "logo": {
          "@type": "ImageObject",
          "url": defaultImage
        }
      }
    }
  ];

  // Add breadcrumb schema if provided (rendered before page-specific schema)
  if (breadcrumb) {
    baseSchemas.push(breadcrumb);
  }

  // Add page-specific schema if provided
  if (schema) {
    baseSchemas.push(schema);
  }

  return (
    <Helmet>
      {/* Basic Metadata */}
      <html lang="zh-TW" />
      <title>{fullTitle}</title>
      <meta name="description" content={description || defaultDescription} />
      <meta name="keywords" content={keywords || defaultKeywords} />

      {/* Canonical URL */}
      <link rel="canonical" href={absoluteCanonicalUrl} />

      {/* Hreflang — 多語系標記 */}
      <link rel="alternate" hreflang="zh-TW" href={absoluteCanonicalUrl} />
      <link rel="alternate" hreflang="zh-CN" href={absoluteCanonicalUrl} />
      <link rel="alternate" hreflang="en" href={absoluteCanonicalUrl} />
      <link rel="alternate" hreflang="ja" href={absoluteCanonicalUrl} />
      <link rel="alternate" hreflang="x-default" href={absoluteCanonicalUrl} />

      {/* Open Graph Tags - Important for Line and Facebook */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description || defaultDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={absoluteCanonicalUrl} />
      <meta property="og:image" content={absoluteImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="zh_TW" />
      <meta property="og:locale:alternate" content="zh_CN" />
      <meta property="og:locale:alternate" content="en_US" />
      <meta property="og:locale:alternate" content="ja_JP" />
      <meta property="og:site_name" content={siteTitle} />

      {/* Line specific meta tags */}
      <meta property="line:card" content="summary_large_image" />
      <meta property="line:image" content={absoluteImageUrl} />

      {/* Twitter Card Tags - Also helps with some social shares */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description || defaultDescription} />
      <meta name="twitter:image" content={absoluteImageUrl} />

      {/* JSON-LD Schemas — base WebSite + page-specific */}
      {baseSchemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;